import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { RelatedChannels } from "@/components/calv/RelatedChannels";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/leistungen/performance-marketing/youtube-demand-gen")({
  head: () => {
    const h = pageHead("pmYoutubeDemandGen", "de");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "YouTube Ads Agentur",
          description: "YouTube TrueView, Demand Gen, Discovery Ads — gesteuert auf wirtschaftlichen Beitrag im integrierten Funnel, nicht auf View-Count.",
          url: "/leistungen/performance-marketing/youtube-demand-gen",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Start", path: "/" },
          { name: "Leistungen", path: "/leistungen" },
          { name: "Performance Marketing", path: "/leistungen/performance-marketing" },
          { name: "YouTube & Demand Gen", path: "/leistungen/performance-marketing/youtube-demand-gen" },
        ])),
      ],
    };
  },
  component: YouTubeDE,
});

const steps = [
  { num: "01", title: "Analyse & Asset-Check (3–5 Tage)", body: "Account-Audit (falls schon YouTube-Aktivität läuft), Bestands-Asset-Analyse (welche Videos sind vorhanden, was fehlt), Funnel-Architektur-Check (wo passt YouTube in den Mix), Wettbewerbs-Analyse. Output: konkrete Hypothesen plus realistisches Asset-Produktions-Briefing." },
  { num: "02", title: "Setup, Tracking & erste Asset-Variationen (1–2 Wochen)", body: "View-Through-Conversion-Setup, Server-side Tracking-Härtung. Account-Struktur an Funnel-Stufen ausgerichtet. Erste TrueView-Kampagnen mit vorhandenen Assets oder picazzoo-Variationen. Demand Gen Setup für Mid-Funnel-Reaktivierung." },
  { num: "03", title: "Iteration & Format-Mix-Optimierung (ab Woche 2–3)", body: "Iteration auf Hook-Variation in den ersten 5 Sekunden (entscheidend bei TrueView), Format-Mix (Bumper, TrueView, Discovery), Audience-Schärfe. Wöchentliche Iteration auf View-Through-Performance plus assistierte Conversions." },
  { num: "04", title: "Skalierung & integriertes Reporting (kontinuierlich)", body: "Budget-Skalierung in funktionierenden Audience-Asset-Kombinationen. Cross-Channel-Reporting im Search- und Direct-Traffic-Kontext — YouTube-Wirkung sichtbar machen, wo sie wirklich passiert. Operative Steuerung in der Regel direkt durch den Geschäftsführer." },
];

const capabilities = [
  { name: "TrueView In-Stream & In-Feed", detail: "Hook-Tests in 5 Sekunden" },
  { name: "Bumper Ads", detail: "6-Sekunden-Reichweite" },
  { name: "Demand Gen Kampagnen", detail: "YouTube · Discover · Gmail" },
  { name: "Discovery Ads", detail: "Native YouTube-Discovery" },
  { name: "View-Through-Conversion-Setup", detail: "Mehrwöchige Tracking-Kette" },
  { name: "Asset-Produktion mit picazzoo", detail: "KI-Video-Variationen" },
  { name: "Audience-Strategien", detail: "Custom Affinity · In-Market" },
  { name: "Cross-Channel-Reporting", detail: "Search + Direct + YouTube" },
];

const audience = [
  { tag: "Primär", title: "B2C-Marken im Aufbau", body: "YouTube ist für junge B2C-Marken oft der wirtschaftlichste Awareness-Kanal — niedrige CPMs für hochwertige Reichweite, plus die Möglichkeit, Brand-Story über Video zu erzählen. Sinnvoll ab Monatsbudgets von etwa €5.000 für YouTube allein." },
  { tag: "Sekundär", title: "Etablierte E-Commerce-Setups mit Awareness-Bedarf", body: "Für E-Commerce-Setups, die Search-Volumen erschöpft haben und Mid-Funnel-Demand generieren wollen. Demand Gen plus TrueView als Search-Komplement. Funktioniert besonders gut für visuelle Produkt-Kategorien." },
  { tag: "Tertiär", title: "SaaS Top-Funnel", body: "YouTube funktioniert für SaaS in der Awareness-Phase — Demo-Videos, Use-Case-Stories, Product-Tours als TrueView. Bottom-Funnel-Conversion bleibt aber typischerweise bei Search." },
  { tag: "Disqualifikation", title: "Wann wir nicht der richtige Partner sind", body: "Bei Monatsbudgets unter €3.000 für YouTube — die Video-Asset-Produktion plus die Awareness-Steuerung lohnen sich nicht. Wenn die Erwartung “virale YouTube-Hits” ist. Wenn keine Video-Assets verfügbar sind und keine produziert werden sollen." },
];

const realistic = [
  "CPM-Werte im DACH-Markt zwischen €4 und €12 für hochwertige Reichweite — abhängig von Audience-Schärfe und Format. Demand Gen oft niedriger als TrueView.",
  "View-Through-Conversion-Lift von 15–30 % auf gesamte Funnel-Performance ist eine typische Bandbreite, wenn YouTube systematisch im Mid-Funnel eingesetzt wird.",
  "Search-Volumen-Hebel von 10–25 % durch YouTube-Awareness-Investments — Demand-Generation-Effekt zwei bis vier Wochen später in Branded und Non-Branded Search messbar.",
  "Asset-Produktions-Effizienz von 3–5x durch picazzoo-Pipeline gegenüber klassischer Designer-Produktion. Das macht YouTube für Mandate wirtschaftlich, in denen Asset-Kosten sonst der Blocker wären.",
];

const value = [
  ["Awareness-Performance-Bridge statt Brand-Vanity.", "Wir reporten auf View-Through-Conversions, assistierte Conversions und Cross-Channel-Wirkung — nicht auf View-Count."],
  ["Asset-Produktion über picazzoo skalierbar.", "Video-Asset-Bottleneck wird zur Calvarius-Stärke."],
  ["Integriertes Funnel-Reporting.", "YouTube-Wirkung sichtbar dort, wo sie wirklich passiert (Search, Direct, später)."],
  ["Format-Mix-Disziplin.", "TrueView, Bumper, Demand Gen, Discovery — passend zur Funnel-Stufe, nicht copy-paste."],
  ["Operative Steuerung durch erfahrene Hand.", "Direkt durch den Geschäftsführer in den meisten Mandaten."],
  ["Vergütungs-Modell ohne Budget-Kopplung.", "Wir verdienen nicht mehr, wenn Sie mehr Budget einsetzen."],
];

const related = [
  { to: "/leistungen/performance-marketing/google-ads", name: "Google Ads", sub: "YouTube läuft technisch über Google Ads — integrierte Steuerung Pflicht" },
  { to: "/leistungen/performance-marketing/tiktok-ads", name: "TikTok Ads", sub: "Komplementärer Video-Performance-Kanal mit anderer Funnel-Logik" },
  { to: "/leistungen/conversion-web-optimierung", name: "Conversion- & Web-Optimierung", sub: "Awareness ohne Conversion-Boden ist verbranntes Geld" },
];

const channelLinks = [
  { to: "/leistungen/performance-marketing/meta-ads", name: "Werbung auf Facebook & Instagram", sub: "Performance auf Instagram und Facebook mit Creative-Disziplin." },
  { to: "/leistungen/performance-marketing/tiktok-ads", name: "TikTok Ads", sub: "Performance auf TikTok jenseits der Influencer-Logik." },
];

function YouTubeDE() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing · YouTube & Demand Gen"
          headline={<>YouTube Ads Agentur — <em>Awareness mit Performance-Logik,</em> nicht Brand-Vanity.</>}
          lead="YouTube TrueView, Demand Gen, Discovery Ads — gesteuert auf wirtschaftlichen Beitrag im integrierten Funnel, nicht auf View-Count. Calvarius arbeitet als YouTube Ads Agentur für B2C-Marken im Aufbau, etablierte E-Commerce-Setups mit Awareness-Bedarf und SaaS-Anbieter im Top-Funnel."
          metaStrip={["Awareness-Performance-Bridge", "View-Through-Steuerung", "Integriert in Google Ads"]}
          breadcrumbs={[
            { label: "Start", to: "/" },
            { label: "Leistungen", to: "/leistungen" },
            { label: "Performance Marketing", to: "/leistungen/performance-marketing" },
            { label: "YouTube & Demand Gen" },
          ]}
        />

        <Section number="02" kicker="Standpunkt" headline={<>Was eine YouTube Ads Agentur mit <em>Performance-Fokus</em> heute leistet.</>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Wer eine YouTube Ads Agentur mit Performance-Fokus sucht, hat in der Regel eine schwierige Spannung erlebt: YouTube ist gleichzeitig ein Awareness-Kanal (mit View-Counts und Brand-Lift als Default-KPIs) und ein Performance-Kanal (mit View-Through-Conversions und Direct-Response-Möglichkeiten). Wer YouTube als reinen Brand-Awareness-Kanal betreibt, lässt Conversion-Hebel liegen. Wer YouTube als reinen Direct-Response-Kanal betreibt, missversteht die Stärken des Mediums. Wer aber Awareness mit Performance-Logik verbindet — also Mid-Funnel-Aktivierung über View-Through-Steuerung systematisch nutzt — kommt in die Zone, in der YouTube wirtschaftlich tragbar wird.</p>
              <p className="mb-5">Calvarius arbeitet mit YouTube und Demand Gen als <strong className="font-semibold text-ink">Awareness-Performance-Bridge</strong>. Das bedeutet: Wir setzen TrueView und Discovery Ads ein, wo wirtschaftlich Sinn macht — nicht überall. Demand Gen ergänzt die klassischen Search-Kampagnen im Mid-Funnel, wo Demand-Generation passiert, bevor jemand aktiv sucht. Wir steuern auf View-Through-Conversions plus assistierte Conversions, nicht auf View-Count oder CPV.</p>
              <p>Konkret heißt das: Über <Link to="/leistungen/picazzoo" className="underline">picazzoo</Link> produzieren wir Video-Asset-Variationen in Markentonalität, wo Designer-Kapazität nicht ausreicht. Wir nutzen YouTube-spezifische Format-Logik — Bumper Ads für Reichweite, TrueView In-Stream für Engagement, Demand Gen für Mid-Funnel-Reaktivierung. Und wir steuern die Cross-Channel-Wirkung — YouTube-Investments wirken oft nicht im YouTube-Konto sichtbar, sondern in Search- und Direct-Traffic-Effekten zwei bis vier Wochen später.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Zielgruppen-Einordnung" headline={<>Wann Sie eine YouTube Ads Agentur <em>brauchen — und wann nicht.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {audience.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Methodik" headline={<>Wie unsere YouTube Ads Agentur <em>Mandate startet.</em></>}>
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-10 max-w-2xl rounded-lg border border-rule bg-bg-subtle p-7">
              <p className="eyebrow mb-3">Schneller Start als Wettbewerbs-Vorteil</p>
              <p className="text-[15px] leading-relaxed text-ink-2">Während klassische Video-Agenturen oft acht bis zwölf Wochen für Setup plus Asset-Produktion brauchen, sind wir innerhalb von zwei bis drei Wochen produktiv — möglich durch picazzoo als Asset-Pipeline und integriertes Account-Setup statt isoliertem Video-Workshop.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="Was wir liefern" headline={<>Was unsere YouTube Ads Agentur <em>konkret liefert.</em></>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="06" kicker="Tooling" headline={<>Tools und Stack unserer <em>YouTube-Steuerung.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Native Plattform</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Google Ads UI (YouTube)</li>
                  <li>YouTube Studio</li>
                  <li>Brand Lift Studies (selektiv)</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Tracking & Analytics</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>View-Through-Conversion-Tracking</li>
                  <li>Server-side via GTM-Server</li>
                  <li>GA4 Cross-Channel-Validierung</li>
                  <li>Looker Studio Funnel-Reporting</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Calvarius-Tools</p>
                <ul className="space-y-3 text-sm text-ink-2">
                  <li><Link to="/leistungen/picazzoo" className="font-medium text-ink hover:text-cyan-deep">picazzoo →</Link><br/><span className="text-ink-3">KI-Video-Asset-Produktion in Markentonalität</span></li>
                </ul>
              </div>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="Was realistisch ist" headline={<>Realistische Ergebnisse einer <em>YouTube-Ads-Optimierung.</em></>}
          caption="YouTube-Wirkung ist mehrwöchig kumulativ — wer auf Wochen-KPIs optimiert, sieht den Wert nicht.">
          <RevealOnScroll>
            <ul className="space-y-4">
              {realistic.map((r, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-6">
                  <span className="font-mono text-xs text-cyan-deep">{String(i+1).padStart(2,"0")}</span>
                  <p className="text-[15px] leading-relaxed text-ink-2">{r}</p>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="Was Sie gewinnen" headline={<>Was Sie von unserer <em>YouTube Ads Agentur</em> bekommen.</>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {value.map(([h, b]) => (
                <div key={h} className="bg-white p-7">
                  <div className="font-display text-lg text-ink">{h}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <RelatedChannels locale="de" items={channelLinks} />

        <Section variant="tinted" number="09" kicker="Weiter vertiefen" headline={<>Verwandte Disziplinen.</>}
          caption="Diese Themen wirken im Zusammenspiel.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]">
                  <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
                  <span aria-hidden className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep">→</span>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/leistungen/performance-marketing" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                ← Alle Performance-Marketing-Disziplinen
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="10" kicker="Konkret werden">
          <CTABlock
            headline={<>YouTube Ads Audit in 30 Minuten — <em>kostenlos und unverbindlich.</em></>}
            body="In einem ersten Gespräch klären wir, ob YouTube Ads in Ihrem Funnel-Setup wirtschaftlich Sinn macht — als Awareness-Performance-Bridge oder als Mid-Funnel-Reaktivierung. Sie bekommen eine ehrliche Einordnung."
            primary={{ label: "Gespräch vereinbaren", to: "/kontakt" }}
            secondary={{ label: "Performance-Marketing-Übersicht", to: "/leistungen/performance-marketing" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
